import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import wtaLogo from "@/assets/wta-logo.png";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;

type Status = "loading" | "ready" | "already" | "invalid" | "submitting" | "done" | "error";

const Unsubscribe = () => {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [status, setStatus] = useState<Status>("loading");

  useEffect(() => {
    if (!token) {
      setStatus("invalid");
      return;
    }
    (async () => {
      try {
        const res = await fetch(
          `${SUPABASE_URL}/functions/v1/handle-email-unsubscribe?token=${encodeURIComponent(token)}`,
          { headers: { apikey: SUPABASE_ANON_KEY } },
        );
        const data = await res.json();
        if (res.ok && data.valid) setStatus("ready");
        else if (data.reason === "already_unsubscribed") setStatus("already");
        else setStatus("invalid");
      } catch {
        setStatus("error");
      }
    })();
  }, [token]);

  const confirm = async () => {
    if (!token) return;
    setStatus("submitting");
    const { data, error } = await supabase.functions.invoke("handle-email-unsubscribe", {
      body: { token },
    });
    if (error) setStatus("error");
    else if (data?.reason === "already_unsubscribed") setStatus("already");
    else setStatus("done");
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6">
      <div className="w-full max-w-md text-center space-y-6">
        <img src={wtaLogo} alt="WTA" className="w-16 h-16 mx-auto object-contain" />
        <h1 className="font-display text-2xl font-bold text-foreground">Email preferences</h1>

        {status === "loading" && (
          <p className="text-muted-foreground font-body text-sm">Verifying your link…</p>
        )}

        {status === "ready" && (
          <>
            <p className="text-muted-foreground font-body text-sm">
              Confirm you'd like to unsubscribe from The Wealth Transfer Academy emails.
            </p>
            <button
              onClick={confirm}
              className="w-full py-3 bg-gradient-gold text-primary-foreground font-body text-sm font-semibold uppercase tracking-[0.2em]"
            >
              Confirm unsubscribe
            </button>
          </>
        )}

        {status === "submitting" && (
          <p className="text-muted-foreground font-body text-sm">Processing…</p>
        )}

        {status === "done" && (
          <p className="text-gold font-body text-sm">You've been unsubscribed. We're sorry to see you go.</p>
        )}

        {status === "already" && (
          <p className="text-gold font-body text-sm">This email is already unsubscribed.</p>
        )}

        {status === "invalid" && (
          <p className="text-destructive font-body text-sm">
            This unsubscribe link is invalid or expired.
          </p>
        )}

        {status === "error" && (
          <p className="text-destructive font-body text-sm">
            Something went wrong. Please try again in a moment.
          </p>
        )}
      </div>
    </div>
  );
};

export default Unsubscribe;
